import type {
  AccessListItem,
  AccessListName,
  AutocompleteOption,
  UserPanelAccess,
  UserPanelAccessPages,
  UserPanelActionResult,
  UserPanelAuthentication,
  UserPanelCapabilities,
  UserPanelResponse,
  UserPanelUser,
  UserPanelWrite,
  UserRole,
  UserStatus,
} from '@app/application/user-management/types'

import type { Ref, WritableComputedRef } from 'vue'

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import {
  createUser,
  fetchAccessPage,
  fetchNewUser,
  fetchUser,
  resetPassword,
  resetTwoFactor as requestTwoFactorReset,
  sendWelcomeMail,
  toggleActive as requestToggleActive,
  updateUser,
} from '@app/application/user-management/api/user-panel'
import {
  ACCESS_LISTS,
  NO_CAPABILITIES,
  NO_WELCOME_MAIL,
  STALE_ACCESS_LIST_REQUEST_REASON,
  STALE_USER_PANEL_REQUEST_REASON,
  UNPROCESSABLE_ENTITY,
  USER_PANEL_REQUEST_KEY,
  WELCOME_MAIL,
} from '@app/application/user-management/constants'
import { abort, getSignal } from '@app/adapters/abort'
import { isResponseError } from '@app/utils/predicates'
import type {
  AccessLists,
  AccessListViews,
  DraftRoleList,
} from '@app/application/user-management/utils/access-lists'
import {
  ACCESS_LIST_NAMES,
  ROLE_LISTS,
  emptyLists,
  groupWrites,
  loadAccessPage,
  removeRow,
  roleWrites,
  viewsOf,
} from '@app/application/user-management/utils/access-lists'
import {
  footerMailActions,
  roleKinds,
  userRoles,
  userStatuses,
} from '@app/application/user-management/types'

const NO_LISTS = emptyLists(false)

export type UserPanelMode = 'edit' | 'create'

export type UserPanelStatus = 'idle' | 'loading' | 'loaded' | 'saving' | 'error'

export interface UserPanelDraft {
  name: string
  email: string
  locale: string
  admin: boolean
  staffAccess: boolean
  companyCreationAccess: boolean
  enableSso: boolean
  ssoEntityId: number | null
  sendWelcomeMail: boolean
}

export type UserPanelFieldErrors = Record<string, string[]>

// `stale` is a save the server took after the panel closed or opened again.
export type UserPanelSaveOutcome = 'saved' | 'stale' | 'failed'

// `users#create` repeats a field's error under the nested global user, and
// adds a bare "is invalid" for each record that holds it. An email taken in the
// firm fails on `globalUserId`, which it also copies onto the email.
const FIELD_ERROR_KEYS = {
  name: ['name', 'globalUser.name'],
  email: ['email', 'globalUser.email'],
}
const RECORD_ERROR_KEYS = [
  'globalUser',
  'globalUser.users',
  'users',
  'globalUserId',
]
const INLINE_ERROR_KEYS = [
  ...Object.values(FIELD_ERROR_KEYS).flat(),
  ...RECORD_ERROR_KEYS,
]

const messagesUnder = (
  errors: UserPanelFieldErrors,
  keys: string[]
): string[] => [...new Set(keys.flatMap((key) => errors[key] ?? []))]

const draftFrom = (response: UserPanelResponse): UserPanelDraft => ({
  name: response.user.name ?? '',
  email: response.user.email ?? '',
  locale: response.user.locale ?? '',
  admin: response.user.admin,
  staffAccess: response.access.staffAccess,
  companyCreationAccess: response.user.companyCreationAccess,
  // Visma Connect owns the requirement for a migrated firm, where the panel
  // draws the choice locked on. The legacy form sends the same value from a
  // hidden field.
  enableSso:
    response.capabilities.ssoLockedByVismaConnect ||
    response.authentication.ssoEnabled,
  ssoEntityId: response.authentication.ssoEntityId,
  // The create form ticks the welcome mail by default. An edit never sends it.
  sendWelcomeMail: response.user.id === null,
})

const fieldErrorsFrom = (error: unknown): UserPanelFieldErrors => {
  if (!isResponseError(error) || error.status !== UNPROCESSABLE_ENTITY) {
    return {}
  }

  const { body } = error

  if (typeof body !== 'object' || body === null || !('errors' in body)) {
    return {}
  }

  return body.errors ?? {}
}

export const useUserPanelStore = defineStore('userPanel', () => {
  const openUserId = ref<number | null>(null)
  const mode = ref<UserPanelMode>('edit')
  const status = ref<UserPanelStatus>('idle')
  const user = ref<UserPanelUser | null>(null)
  const loadedCapabilities = ref<UserPanelCapabilities | null>(null)
  const access = ref<UserPanelAccess | null>(null)
  const authentication = ref<UserPanelAuthentication | null>(null)
  const draft = ref<UserPanelDraft | null>(null)
  const lists = ref<AccessLists | null>(null)
  const errors = ref<UserPanelFieldErrors>({})
  const warning = ref<string | null>(null)
  const resetting = ref(false)
  const toggling = ref(false)
  const mailing = ref(false)
  const actionError = ref<string | null>(null)

  // What the panel was loaded with, so an edit can be told from an untouched
  // field.
  const loaded = ref<UserPanelDraft | null>(null)
  const newUserExternal = ref(false)

  const capabilities = computed(
    () => loadedCapabilities.value ?? NO_CAPABILITIES
  )

  // An inactive user has no permissions to choose between (`form.html.slim`
  // hides every permission section), and an external user gets the Contributor
  // and Pulse choice instead.
  const rolesVisible = computed(
    () => (user.value?.isActive ?? false) && !user.value?.external
  )

  // The backend reads an external user without roles as a contributor, but a
  // new external user can only be created as Pulse: contributors are added
  // from a file.
  const externalRole = computed<UserRole | null>(() => {
    if (!user.value?.external) return null

    return mode.value === 'create' || user.value.role === userRoles.PULSE
      ? userRoles.PULSE
      : userRoles.CONTRIBUTOR
  })

  const companyCreationVisible = computed(
    () =>
      capabilities.value.companyCreationAvailable &&
      draft.value?.admin === false
  )

  // The first option is the firm default, so a firm with a single locale leaves
  // nothing to choose between.
  const languageVisible = computed(
    () =>
      capabilities.value.localeOptions.filter((option) => option.value !== '')
        .length > 1
  )

  const isOpen = computed(() => status.value !== 'idle')

  const creating = computed(() => mode.value === 'create')

  // One request at a time from the footer, so a save cannot answer with the
  // active flag a toggle in flight is about to change.
  const busy = computed(
    () => status.value !== 'loaded' || toggling.value || mailing.value
  )

  const isDirty = computed(
    () =>
      JSON.stringify(draft.value) !== JSON.stringify(loaded.value) ||
      Object.values(lists.value ?? {}).some(
        (list) => list.added.length > 0 || list.removed.length > 0
      )
  )

  const accessViews = computed<AccessListViews>(() =>
    viewsOf(lists.value ?? NO_LISTS)
  )

  // The lists the panel draws, the only ones worth a request. An inactive user
  // shows no permission section (`form.html.slim` hides them), and a Pulse user
  // holds no client file.
  const shownLists = computed<AccessListName[]>(() => {
    if (!user.value?.isActive) return []
    if (externalRole.value === userRoles.PULSE) return ['customerRoles']
    if (user.value.external) return ['externalRoles', 'customerRoles']

    return [
      'firmRoles',
      ...(capabilities.value.groupsSupported ? ['groups' as const] : []),
      'customerRoles',
    ]
  })

  const nameErrors = computed(() =>
    messagesUnder(errors.value, FIELD_ERROR_KEYS.name)
  )

  const emailErrors = computed(() =>
    messagesUnder(errors.value, FIELD_ERROR_KEYS.email)
  )

  // The list at the top of the panel holds what no field shows next to itself.
  const errorMessages = computed(() =>
    messagesUnder(
      errors.value,
      Object.keys(errors.value).filter(
        (key) => !INLINE_ERROR_KEYS.includes(key)
      )
    )
  )

  const hasErrors = computed(
    () =>
      errorMessages.value.length > 0 ||
      nameErrors.value.length > 0 ||
      emailErrors.value.length > 0
  )

  // The table derives the same three states from the same two fields
  // (`UserManagement::UserRow#status`).
  const userStatus = computed<UserStatus | null>(() => {
    // A user who does not exist yet has no state to report.
    if (!user.value || mode.value === 'create') return null
    if (!user.value.isActive) return userStatuses.DEACTIVATED
    if (!user.value.confirmed) return userStatuses.UNCONFIRMED

    return userStatuses.ACTIVE
  })

  function abortListRequests(): void {
    ACCESS_LIST_NAMES.forEach((name) =>
      abort(ACCESS_LISTS[name].requestKey, STALE_ACCESS_LIST_REQUEST_REASON)
    )
  }

  // Reactivating a user shows lists the panel had no reason to load before.
  function loadShownLists(): void {
    shownLists.value.forEach((name) => {
      if (!lists.value?.[name].loaded) void fetchList(name, '', 1)
    })
  }

  function apply(response: UserPanelResponse): void {
    abortListRequests()
    user.value = response.user
    loadedCapabilities.value = response.capabilities
    authentication.value = response.authentication
    access.value = response.access
    draft.value = draftFrom(response)
    loaded.value = draftFrom(response)
    lists.value = emptyLists(response.user.id !== null)
    warning.value = response.warning ?? null
    openUserId.value = response.user.id
    loadShownLists()
  }

  async function load(
    fetcher: () => Promise<UserPanelResponse | null>
  ): Promise<void> {
    // A second row clicked while the first is still in flight cancels the
    // first, so the slower answer cannot overwrite the newer one.
    abort(USER_PANEL_REQUEST_KEY, STALE_USER_PANEL_REQUEST_REASON)

    const signal = getSignal(USER_PANEL_REQUEST_KEY)
    const isStale = (): boolean => signal?.aborted === true

    status.value = 'loading'
    errors.value = {}
    warning.value = null
    actionError.value = null

    try {
      const response = await fetcher()

      if (isStale()) return

      if (response) {
        apply(response)
        status.value = 'loaded'
      } else {
        status.value = 'error'
      }
    } catch {
      if (isStale()) return

      status.value = 'error'
    }
  }

  async function open(id: number): Promise<void> {
    mode.value = 'edit'
    openUserId.value = id

    await load(() => fetchUser(id))
  }

  async function openNew(external: boolean): Promise<void> {
    mode.value = 'create'
    openUserId.value = null
    newUserExternal.value = external

    await load(() => fetchNewUser(external))
  }

  async function retry(): Promise<void> {
    if (mode.value === 'create') {
      await openNew(newUserExternal.value)
    } else if (openUserId.value !== null) {
      await open(openUserId.value)
    }
  }

  function close(): void {
    abort(USER_PANEL_REQUEST_KEY, STALE_USER_PANEL_REQUEST_REASON)
    abortListRequests()
    status.value = 'idle'
    openUserId.value = null
    user.value = null
    loadedCapabilities.value = null
    authentication.value = null
    access.value = null
    draft.value = null
    loaded.value = null
    lists.value = null
    errors.value = {}
    warning.value = null
    actionError.value = null
  }

  function draftField<K extends keyof UserPanelDraft>(
    key: K
  ): WritableComputedRef<UserPanelDraft[K] | undefined> {
    return computed({
      get: () => draft.value?.[key],
      set: (value) => {
        if (draft.value && value !== undefined) draft.value[key] = value
      },
    })
  }

  function fetchList<L extends AccessListName>(
    name: L,
    query: string,
    page: number
  ): Promise<void> {
    const list: AccessLists[L] | undefined = lists.value?.[name]
    const userId = openUserId.value

    // A user being created has nothing saved to page through.
    return loadAccessPage(
      list,
      ACCESS_LISTS[name].requestKey,
      query,
      page,
      userId === null
        ? null
        : (signal): Promise<UserPanelAccessPages[L] | null> =>
            fetchAccessPage(userId, name, query, page, signal)
    )
  }

  function searchAccess(name: AccessListName, query: string): Promise<void> {
    return fetchList(name, query, 1)
  }

  // The next page belongs to the query the loaded rows answer, so a search in
  // flight wins.
  function loadMoreAccess(name: AccessListName): Promise<void> {
    const list = lists.value?.[name]

    if (!list || list.loading) return Promise.resolve()

    return fetchList(name, list.query, list.page + 1)
  }

  // `Role` allows one role per user and company, and the save sends the adds
  // before the removals, so a company in any list fails the save, even one
  // removed in the panel. `SaveUser` skips that add without an error. The search
  // leaves out the files the user holds, so only the loaded and added rows are
  // left to check.
  function addRole(list: DraftRoleList, option: AutocompleteOption): void {
    const current = lists.value

    if (
      !current ||
      ROLE_LISTS.some((key) =>
        [...current[key].added, ...current[key].rows].some(
          (role) => role.companyId === option.id
        )
      )
    ) {
      return
    }

    current[list].added.unshift({
      id: null,
      companyId: option.id,
      companyName: option.name,
    })
  }

  function removeRole(list: DraftRoleList, key: AccessListItem['key']): void {
    if (lists.value) removeRow(lists.value[list], key)
  }

  function addGroup(option: AutocompleteOption): void {
    const groups = lists.value?.groups

    if (
      !groups ||
      [...groups.added, ...groups.rows].some((group) => group.id === option.id)
    ) {
      return
    }

    groups.added.unshift({
      id: option.id,
      name: option.name,
      usersCount: 0,
      removable: true,
    })
  }

  function removeGroup(id: AccessListItem['key']): void {
    if (lists.value) removeRow(lists.value.groups, id)
  }

  function writePayload(): UserPanelWrite {
    const current = draft.value as UserPanelDraft
    const access = lists.value as AccessLists
    // Every SSO write queues a global database job, so an unchanged value is
    // left out. The comparison is with the server, not the loaded draft: a
    // firm on Visma Connect draws SSO on while the user may not hold it yet.
    const ssoChanged =
      current.enableSso !== authentication.value?.ssoEnabled ||
      current.ssoEntityId !== authentication.value?.ssoEntityId

    return {
      name: current.name,
      locale: current.locale,
      admin: current.admin,
      staffAccess: current.staffAccess,
      companyCreationAccess: current.companyCreationAccess,
      // `users#create` reads the email out of the nested attributes before
      // anything else runs, so they travel with every create.
      ...(creating.value || (capabilities.value.ssoSupported && ssoChanged)
        ? {
            globalUserAttributes: {
              ...(creating.value ? { email: current.email } : {}),
              enableSso: current.enableSso,
              firmAccessSsoEntityId: current.ssoEntityId,
            },
          }
        : {}),
      ...(creating.value
        ? {
            external: newUserExternal.value,
            sendWelcomeMail: current.sendWelcomeMail
              ? WELCOME_MAIL
              : NO_WELCOME_MAIL,
          }
        : {}),
      roles: [
        ...roleWrites(roleKinds.FIRM, access.firmRoles),
        ...roleWrites(roleKinds.EXTERNAL, access.externalRoles),
        ...roleWrites(roleKinds.CUSTOMER, access.customerRoles),
      ],
      groups: groupWrites(access.groups),
    }
  }

  async function save(): Promise<UserPanelSaveOutcome> {
    const id = openUserId.value

    if (
      status.value !== 'loaded' ||
      !draft.value ||
      (!creating.value && id === null)
    ) {
      return 'failed'
    }

    // The save is not cancelled, but a close or another row opened meanwhile
    // must not have its panel overwritten by this answer.
    const signal = getSignal(USER_PANEL_REQUEST_KEY)
    const isStale = (): boolean => signal?.aborted === true

    status.value = 'saving'
    errors.value = {}
    warning.value = null
    actionError.value = null

    try {
      const payload = writePayload()
      const response =
        id === null ? await createUser(payload) : await updateUser(id, payload)

      if (isStale()) return response ? 'stale' : 'failed'

      status.value = 'loaded'

      if (!response) return 'failed'

      // A create closes the panel, so reading the answer back into the draft
      // would only write the new id into the query string on the way out.
      if (!creating.value) apply(response)

      return 'saved'
    } catch (error) {
      if (isStale()) return 'failed'

      status.value = 'loaded'
      errors.value = fieldErrorsFrom(error)

      return 'failed'
    }
  }

  async function resetTwoFactor(): Promise<UserPanelActionResult | null> {
    if (openUserId.value === null) return null

    resetting.value = true

    try {
      return await requestTwoFactorReset(openUserId.value)
    } catch {
      return null
    } finally {
      resetting.value = false
    }
  }

  // A refused footer action answers 422 with the guard's own sentence under the
  // same `errors` shape a rejected save uses. Anything else carries no message.
  function refusalFrom(error: unknown): string | null {
    return fieldErrorsFrom(error).base?.[0] ?? null
  }

  async function runAction(
    action: (id: number) => Promise<UserPanelActionResult | null>,
    running: Ref<boolean>,
    afterSuccess?: (id: number, isStale: () => boolean) => Promise<void>
  ): Promise<UserPanelActionResult | null> {
    const id = openUserId.value

    if (id === null) return null

    // A close or another row opened meanwhile must not have its panel changed
    // by this answer. The result still goes back, since the action ran.
    const signal = getSignal(USER_PANEL_REQUEST_KEY)
    const isStale = (): boolean => signal?.aborted === true

    actionError.value = null
    running.value = true

    try {
      const result = await action(id)

      if (result && !isStale()) await afterSuccess?.(id, isStale)

      return result
    } catch (error) {
      if (!isStale()) actionError.value = refusalFrom(error)

      return null
    } finally {
      running.value = false
    }
  }

  async function toggleActive(): Promise<UserPanelActionResult | null> {
    const nextIsActive = !user.value?.isActive

    return runAction(
      (id) => requestToggleActive(id, nextIsActive),
      toggling,
      async (id, isStale) => {
        // The deactivation guard also reads the active flag, so the panel reads
        // the user back rather than flipping the flag on its own.
        const response = await fetchUser(id).catch(() => null)

        if (isStale()) return

        if (response) {
          user.value = response.user
          loadedCapabilities.value = response.capabilities
        } else if (user.value) {
          user.value.isActive = nextIsActive
        }

        loadShownLists()
      }
    )
  }

  async function sendFooterMail(): Promise<UserPanelActionResult | null> {
    return runAction(
      capabilities.value.footerMailAction === footerMailActions.WELCOME_MAIL
        ? sendWelcomeMail
        : resetPassword,
      mailing
    )
  }

  return {
    access,
    accessViews,
    actionError,
    addGroup,
    addRole,
    authentication,
    busy,
    capabilities,
    close,
    creating,
    companyCreationVisible,
    draft,
    draftField,
    emailErrors,
    errorMessages,
    errors,
    externalRole,
    hasErrors,
    isDirty,
    isOpen,
    mailing,
    languageVisible,
    loadMoreAccess,
    mode,
    nameErrors,
    open,
    openNew,
    openUserId,
    removeGroup,
    removeRole,
    resetTwoFactor,
    resetting,
    retry,
    rolesVisible,
    save,
    searchAccess,
    sendFooterMail,
    shownLists,
    status,
    toggleActive,
    toggling,
    user,
    userStatus,
    warning,
  }
})
